import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  Badge,
  CONFIDENCE_THRESHOLDS,
  ConfirmCancelButtons,
  EnvironmentBadge,
  SkeletonLoader,
  Tooltip,
  useEnvironmentSuggestion,
  useUpdateIntegrationEnvironment,
} from '../../../main';

import { useTranslation } from 'react-i18next';

export type EnvironmentSuggestionProps = {
  /** `Integration Id` */
  integrationId: string;
  /** Minimum confidence level required to show the suggestion. Defaults to 'high' */
  minConfidenceLevel?: 'high' | 'medium' | 'low';
  /** When set, suggestion is hidden (environment already set) */
  currentEnvironmentValue?: string | null;
  /** When false, the suggestion block is not rendered. Defaults to true. */
  showSuggestion?: boolean;
};

/**
 * EnvironmentSuggestion displays the suggested environment classification of the given integration
 *
 * Example:
 * ```tsx
 *   <EnvironmentSuggestion integrationId={integrationId} />
 * ```
 */

export const EnvironmentSuggestion = ({
  integrationId,
  minConfidenceLevel = 'high',
  currentEnvironmentValue,
  showSuggestion = true,
}: EnvironmentSuggestionProps) => {
  const { t } = useTranslation('designSystem');
  const { data: suggestion, status } = useEnvironmentSuggestion(integrationId);
  const { mutateAsync: updateEnvironment } = useUpdateIntegrationEnvironment();

  const [isSuggestionDismissed, setIsSuggestionDismissed] = useState(() =>
    getDismissedSuggestions().includes(integrationId),
  );
  const { environment, environmentId, confidence, reasoning } =
    suggestion ?? {};

  const isEnvironmentSet =
    currentEnvironmentValue !== undefined &&
    currentEnvironmentValue !== null &&
    currentEnvironmentValue !== '';

  const threshold = CONFIDENCE_THRESHOLDS[minConfidenceLevel];
  const confidenceNum =
    typeof confidence === 'number' ? confidence : Number(confidence);
  const meetsConfidence =
    !Number.isNaN(confidenceNum) && confidenceNum >= threshold;
  const showBanner =
    !isEnvironmentSet &&
    !isSuggestionDismissed &&
    meetsConfidence &&
    Boolean(environmentId) &&
    Boolean(environment);

  const dismissSuggestion = () => {
    addDismissedSuggestion(integrationId);
    setIsSuggestionDismissed(true);
  };

  const handleConfirmSuggestion = async () => {
    if (!environmentId) {
      return;
    }
    const id = toast.loading(t('settingEnvironment'));
    try {
      await updateEnvironment({ integrationId, environmentId });
      toast.success(t('environmentSuccessfullyUpdated'), { id });
    } catch {
      toast.error(t('failedToSetEnvironmentForIntegration'), { id });
    }
  };

  if (!showSuggestion) {
    return null;
  }

  return (
    <SkeletonLoader status={status}>
      {showBanner ? (
        <div className="flex min-w-0 flex-row flex-nowrap items-center gap-7 rounded-xl p-3">
          <div className="flex flex-col justify-center gap-1">
            <span className="flex items-center gap-1.5 text-xs">
              {t('common:suggestedEnvironment')}
              {reasoning && (
                <Tooltip label={t('reason', { reasoning })} placement="top">
                  <span className="inline-flex cursor-help items-center">
                    <InformationCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </span>
                </Tooltip>
              )}
            </span>
            {environment ? (
              <EnvironmentBadge value={environment} />
            ) : (
              <Badge label={t('environmentUnknown')} status="neutral" />
            )}
          </div>
          <div className="flex shrink-0 items-center">
            <ConfirmCancelButtons
              cancelLabel={t('dismiss')}
              confirmLabel={t('common:applySuggestion')}
              onCancel={dismissSuggestion}
              onConfirm={handleConfirmSuggestion}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
    </SkeletonLoader>
  );
};

const DISMISSED_SUGGESTIONS_KEY = 'environment-suggestions-dismissed';

const getDismissedSuggestions = (): string[] => {
  try {
    const localStoreDismissedSuggestions = localStorage.getItem(
      DISMISSED_SUGGESTIONS_KEY,
    );
    if (!localStoreDismissedSuggestions) {
      return [];
    }

    const dismissedSuggestions: unknown = JSON.parse(
      localStoreDismissedSuggestions,
    );
    if (
      Array.isArray(dismissedSuggestions) &&
      dismissedSuggestions.every((id) => typeof id === 'string')
    ) {
      return dismissedSuggestions;
    }
    return [];
  } catch {
    console.error('Failed to parse dismissed suggestions');
    localStorage.removeItem(DISMISSED_SUGGESTIONS_KEY);
    return [];
  }
};

const addDismissedSuggestion = (integrationId: string) => {
  const dismissedSuggestions = getDismissedSuggestions();
  if (!dismissedSuggestions.includes(integrationId)) {
    dismissedSuggestions.push(integrationId);
    localStorage.setItem(
      DISMISSED_SUGGESTIONS_KEY,
      JSON.stringify(dismissedSuggestions),
    );
  }
};
